import React, { useEffect, useState, useMemo } from 'react'
import { Flex, Icon, Box, css } from '@storyofams/react-ui'
import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit'
import { AnimatePresence, motion } from 'framer-motion'
import { endsWith } from 'lodash'
import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import { defineMessages, useIntl, FormattedMessage } from 'react-intl'
import { Container, Seo, Heading, Button, Text, FixedRatio } from '~components'
import { useShopify } from '~context'
import { useFilters } from '~hooks'
import {
  enhancedStaticProps,
  withLayout,
  formatPrice,
  gaViewListItemsEvent,
} from '~lib'
import { Cross, NewFiterIcon, MobileFilterIcon } from '~components/common/Icons'
import { BottomBar, Filters, ProductsInGroupsGrid } from '~components/meals'
import { getShopifySdk } from '~lib/shopify/client'
import { ProductTagsQuery } from '~lib/shopify/sdk'
import { shopifyDiscountPriceFactor } from '~config'
import { MealssettingsComponent } from '~lib/storyblok/sdk'
import { Image } from '@storyofams/storyblok-toolkit'

interface ProductListingPageProps extends WithStoryProps {
  tags: ProductTagsQuery['productTags']
  mealsSettings: MealssettingsComponent
}

const messages = defineMessages({
  seoTitle: 'Discover our meals',
  selectMeals: 'Select your meals',
  discoverMeals: 'Discover our meals',
  boxSelection: 'box selection',
  filters: 'Filters',
  next: 'Next',
  yourMealBox: 'Your meal box',
  select6Meals: 'Select at least 8 meals',
  home: 'home',
})

const ProductListingPage: NextPage<ProductListingPageProps> = ({
  tags,
  mealsSettings,
}) => {
  const router = useRouter()
  const intl = useIntl()
  const {
    // isShowingMobileFilters,
    // setIsShowingMobileFilters,
    // isShowingDesktopFilters,
    // setIsShowingDesktopFilters,
    isShowingFilters,
    setIsShowingFilters,
    parsedQuery,
    handleFilterChange,
    handleReset,
    products,
    isLoading,
    error,
  } = useFilters(tags)

  const productsCount = useMemo(
    () => (!!products ? products.edges.length : 0),
    [products],
  )

  useEffect(() => {
    if (productsCount > 0) {
      const productNodes = products.edges.map(el => el.node)
      let items = []

      productNodes.forEach(pn => {
        pn.variants.edges.forEach(pnve => {
          items.push({
            item_id: pnve?.node?.sku,
            item_name: pnve?.node?.product?.title,
            item_brand: pnve?.node?.product?.vendor,
            item_variant: pnve?.node?.title,
            price: Number(pnve?.node?.priceV2?.amount),
            quantity: 1,
          })
        })
      })

      gaViewListItemsEvent({
        list_name: 'Meals page',
        items: items,
      })
    }
  }, [productsCount])

  useEffect(() => {
    let groups = {}
    if (!!mealsSettings && !!products) {
      const mealsGroups = mealsSettings?.meals_groups || []
      mealsGroups.forEach(el => {
        groups[el.tag] = {}
        groups[el.tag].id = el._uid
        groups[el.tag].title = el?.title
        groups[el.tag].sub_title = el?.sub_title
        groups[el.tag].image = el?.image?.filename
        groups[el.tag].image_bg = el?.image_bg?.color
        groups[el.tag].products = []
      })
      groups['without_group'] = {}
      groups['without_group'].products = []

      if (Object.keys(groups).length > 0 && products?.edges.length > 0) {
        products?.edges.forEach(({ node: product }) => {
          let productWithoutGroup = true
          Object.keys(groups).forEach(group => {
            if (product?.tags.includes(group)) {
              productWithoutGroup = false
              groups[group].products.push(product)
            }
          })

          if (productWithoutGroup) {
            groups['without_group'].products.push(product)
          }
        })
      }

      setProductsInGroups(groups)
    }
  }, [mealsSettings, products])

  const { cart, portion, setPortion, quantity } = useShopify()
  const [totalAmountWithDiscount, setTotalAmountWithDiscount] = useState(0)
  const [productsInGroups, setProductsInGroups] = useState({})
  if (
    router?.query?.ordering &&
    !!cart &&
    !cart?.lines?.edges?.length &&
    !portion
  ) {
    setPortion('selectable')
  }

  useEffect(() => {
    let totalAmount = 0

    if (!!cart) {
      cart?.lines?.edges.forEach(el => {
        const productPrice = (
          el?.node?.merchandise?.priceV2?.amount * shopifyDiscountPriceFactor
        ).toFixed(2)
        totalAmount = totalAmount + el?.node?.quantity * Number(productPrice)
      })
    }

    setTotalAmountWithDiscount(totalAmount)
  }, [cart, quantity])

  const scrollToSection = id => {
    const el = document?.getElementById(id)
    const y = el.getBoundingClientRect().top - 90

    if (!!el) {
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  return (
    <>
      <Seo title={intl.formatMessage(messages.seoTitle)} />

      {/* <Header
        hideOnMobile={true}
        title={
          router?.query?.ordering === 'true'
            ? intl.formatMessage(messages.selectMeals)
            : intl.formatMessage(messages.discoverMeals)
        }
      >
        <Box width="100%" display={['none', 'none', 'none', 'block']} position="relative" pb={!isShowingDesktopFilters && 5}>
          <Button
            variant="outline"
            position="absolute"
            right={0}
            ml={[0, 0, "auto"]}
            mb={4}
            px={isShowingDesktopFilters && 2}
            width={["100%", '100%', 'auto']}
            onClick={() => setIsShowingDesktopFilters(!isShowingDesktopFilters)}
            transition="all .15s ease-in-out"
          >
              {!isShowingDesktopFilters && intl.formatMessage(messages.filters)}
            <Icon icon={isShowingDesktopFilters ? Cross : FiltersIcon} fontSize={2} ml={!isShowingDesktopFilters && 4} />
          </Button>

          <Filters
            filtersMapping={tags?.edges
              ?.map(({ node }) => node)
              .filter((f) => !endsWith(f, portion === 'xl' ? 'selectable' : 'xl'))}
            isShowingDesktopFilters={isShowingDesktopFilters}
            setIsShowingDesktopFilters={setIsShowingDesktopFilters}
            isShowingMobileFilters={isShowingMobileFilters}
            setIsShowingMobileFilters={setIsShowingMobileFilters}
            parsedQuery={parsedQuery}
            handleFilterChange={handleFilterChange}
            handleReset={handleReset}
            products={products}
          />
        </Box>
      </Header> */}
      <Filters
        filtersMapping={tags?.edges
          ?.map(({ node }) => node)
          .filter(f => !endsWith(f, 'selectable'))}
        isShowingFilters={isShowingFilters}
        setIsShowingFilters={setIsShowingFilters}
        parsedQuery={parsedQuery}
        handleFilterChange={handleFilterChange}
        handleReset={handleReset}
        products={products}
      />
      <Container alignItems="center" py={[3, 4, 9]} space={0}>
        {!!mealsSettings?.title && (
          <Heading
            variant="sh1"
            as="h1"
            color={'#21857F'}
            alignSelf={'flex-start'}
          >
            {mealsSettings?.title}
          </Heading>
        )}

        <Flex
          width={'100vw'}
          gap={['20px', '27px']}
          pt={[2]}
          px={{
            all: 2,
            md: 8,
            lg: 15,
          }}
          position={'sticky'}
          top={['52px', '52px', '80px']}
          zIndex={'2'}
          bg={'#FAF7F5'}
        >
          <Flex
            overflowX={['auto', 'hidden']}
            gap={['20px']}
            pb={2}
            flexWrap={['nowrap', 'wrap']}
            css={css({
              '&': {
                'row-gap': '10px',
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none',
              },
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            })}
          >
            <Button
              onClick={() => setIsShowingFilters(true)}
              height={'60px'}
              backgroundColor={'#FFFFFF'}
              borderRadius={'8px'}
              boxShadow={'0px 4px 8px rgba(22, 22, 21, 0.15)'}
              variant={'unstyled'}
              p={2}
              display={'flex'}
              alignItems={'center'}
            >
              <Icon
                css={`
                  @media screen and (max-width: 1024px) {
                    display: none;
                  }
                `}
                icon={NewFiterIcon}
                fontSize={'30px'}
                mr={[0, 3]}
              />
              <Icon
                css={`
                  @media screen and (min-width: 1025px) {
                    display: none;
                  }
                `}
                icon={MobileFilterIcon}
                fontSize={'30px'}
              />
              <Heading
                display={['none', 'block']}
                pr={1}
                variant="sh4"
                as="h4"
                fontSize={'20px'}
                lineHeight={'23px'}
                css={`
                  @media screen and (max-width: 1024px) {
                    display: none;
                  }
                `}
              >
                {intl.formatMessage(messages.filters)}
              </Heading>
            </Button>

            {!!mealsSettings?.meals_groups && (
              <>
                {mealsSettings?.meals_groups.map(el => {
                  const tag = el?.tag
                  const productsArePresent =
                    productsInGroups[tag]?.products?.length > 0

                  if (productsArePresent) {
                    return (
                      <Button
                        key={`anchor-button-${el?._uid}`}
                        height={['auto', '60px']}
                        maxWidth={['60px', 'fit-content']}
                        backgroundColor={['transparent', '#fff']}
                        borderRadius={'8px'}
                        boxShadow={[
                          'none',
                          '0px 4px 8px rgba(22, 22, 21, 0.15)',
                        ]}
                        variant={'unstyled'}
                        onClick={() => scrollToSection(el?._uid)}
                      >
                        <Flex
                          width={'100%'}
                          flexDirection={['column', 'row']}
                          gap={['10px', '0']}
                          alignItems={'center'}
                          textAlign={'center'}
                        >
                          <Box
                            width={'60px'}
                            p={'3px'}
                            bg={el?.image_bg?.color}
                            borderRadius={'8px'}
                            borderTopRightRadius={['8px', '0']}
                            borderBottomRightRadius={['8px', '0']}
                            boxShadow={[
                              '0px 4px 8px rgba(22, 22, 21, 0.15)',
                              'none',
                            ]}
                          >
                            <FixedRatio ratio={[[1, 1]]} overflow="hidden">
                              <Image
                                alt={el?.image?.alt || ''}
                                src={el?.image?.filename}
                                fluid={500}
                                fit="cover"
                              />
                            </FixedRatio>
                          </Box>
                          <Box
                            display={['flex', 'none']}
                            alignItems={'center'}
                            justifyContent={'center'}
                            fontSize={'12px'}
                            css={css({
                              '&': {
                                'word-break': 'break-all',
                              },
                            })}
                          >
                            {el?.title}
                          </Box>
                          <Box display={['none', 'flex']}>
                            <Heading
                              pl={'18px'}
                              pr={'24px'}
                              variant="sh4"
                              as="h4"
                              fontSize={'20px'}
                              lineHeight={'23px'}
                            >
                              {el?.title}
                            </Heading>
                          </Box>
                        </Flex>
                      </Button>
                    )
                  } else {
                    return null
                  }
                })}
              </>
            )}
          </Flex>
        </Flex>

        <Flex flexDirection="column" alignItems="center" width="100%" pt={4}>
          <ProductsInGroupsGrid
            productsInGroups={productsInGroups}
            isLoading={isLoading}
            error={error}
            resetFilters={handleReset}
            hasFilters={!!Object.keys(parsedQuery)?.length}
            withControls
          />
        </Flex>

        <Flex
          flexDirection="column"
          width="100%"
          css={css({
            '.static-bar': {
              '.text-wrapper': {
                alignItems: ['center', 'flex-start'],
              },
            },
          })}
        >
          <BottomBar
            buttonProps={{
              onClick: () => {
                router?.push('/order/overview')
              },
              variant: 'button_type_1',
              disabled: quantity < 8,
              label: `${intl.formatMessage(messages.next)} - ${
                !!cart
                  ? formatPrice({
                      ...cart?.cost?.totalAmount,
                      amount: totalAmountWithDiscount,
                    })
                  : '---'
              }`,
            }}
            noDivider={true}
          >
            <Flex
              flex={1}
              justifyContent="space-between"
              width="100%"
              alignItems="center"
            >
              <Flex
                flexDirection="column"
                alignItems="flex-start"
                width="100%"
                className="text-wrapper"
              >
                <Text
                  fontSize={[1.5, 2.25]}
                  fontWeight={['normal', 'bold']}
                  as="span"
                  display={['none', 'flex']}
                >
                  <FormattedMessage
                    defaultMessage="{quantity} meals selected"
                    values={{ quantity }}
                  />
                </Text>

                <Text
                  fontSize={[1.5, 2.25]}
                  fontWeight={['normal', 'bold']}
                  display={['flex', 'none']}
                >
                  {quantity >= 8 ? (
                    <FormattedMessage
                      defaultMessage="{quantity} meals selected"
                      values={{ quantity }}
                    />
                  ) : (
                    <FormattedMessage {...messages.select6Meals} />
                  )}
                </Text>
              </Flex>

              {quantity < 8 && (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ ease: 'easeInOut', duration: 0.24 }}
                  >
                    <Text display={['none', 'flex']} mr={3}>
                      <FormattedMessage {...messages.select6Meals} />
                    </Text>
                  </motion.div>
                </AnimatePresence>
              )}
            </Flex>
          </BottomBar>
        </Flex>
      </Container>
    </>
  )
}

export default withLayout(withStory(ProductListingPage))

export const getStaticProps: GetStaticProps<ProductListingPageProps> =
  enhancedStaticProps(async ({ sdk, locale, defaultLocale }) => {
    const shopifySdk = getShopifySdk(locale)

    const { productTags } = await shopifySdk.productTags()
    const { MealssettingsItem } = await sdk.mealssettings({
      slug: `${
        defaultLocale !== locale ? `${locale}/settings/` : 'settings/'
      }meals-settings`,
    })

    return {
      props: {
        tags: productTags,
        mealsSettings: MealssettingsItem?.content,
      },
      revalidate:
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' ? 1 : 60,
    }
  })
